import { Suspense, useEffect, useState } from 'react';
import './App.css';
import Header from './components/core/header/Header';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Amplify, Auth } from 'aws-amplify';
import { cognitoConfig } from './utils/AuthConfig/CognitoConfig';
import { checkAppAccessibility } from './utils/accessibility-check';
import PermissionError from './components/error/permission-error/PermissionError';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { TranslateEn } from './assets/i18n/translateEn';
import { TranslateFr } from './assets/i18n/translateFr';
import { ToastContainer } from 'react-toastify';
import AppRoutes from './utils/routes';
import { postLoginLogout } from './services/UserAuthorization';
import { FeatureProvider, useFeatures } from './utils/featureContext';


Amplify.configure(cognitoConfig);

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: TranslateEn },
    fr: { translation: TranslateFr }
  },
  lng: "en",
  fallbackLng: "en",
  interpolation: { escapeValue: false }
});

const LOGIN_AUDIT_KEY = "LOGIN_AUDIT_DONE";

const getUserNameFromEmail = (email) => {
  if (!email || typeof email !== "string") return "";
  return email.split("@")[0];
};

const FeatureLoader = ({ userName, children }) => {
  const { loadFeatures } = useFeatures();

  useEffect(() => {
    if (userName) {
      loadFeatures(userName);
    }
  }, [userName, loadFeatures]);

  return children;
};

function CApp() {

  const [userSession, setUserSession] = useState(false);
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState("");


  useEffect(() => {
    let isMounted = true;

    Auth.currentAuthenticatedUser()
      .then((userInfo) => {
        if (!isMounted) return;

        console.log("User", userInfo);
        const attributes = userInfo.attributes;
        const extractedUserName = getUserNameFromEmail(attributes.email);

        setUser(attributes);
        setUserName(extractedUserName);

        const alreadyLogged = sessionStorage.getItem(LOGIN_AUDIT_KEY);

        if (!alreadyLogged) {
          postLoginLogout({
            action: "login",
            userName: extractedUserName,
          })
            .then(() => {
              sessionStorage.setItem(LOGIN_AUDIT_KEY, "true");
            })
            .catch((err) => {
              console.error("Login audit failed", err);
            });
        }

        return Auth.currentSession();
      })
      .then((session) => {
        if (!isMounted) return;
        setUserSession(session);
      })
      .catch(() => {
        Auth.federatedSignIn();
      });

    return () => {
      isMounted = false;
    };
  }, []);



  // Check if user has permission to access the page
  const canUserAccess = () => {
    return user && checkAppAccessibility(user['custom:UserType'], user['custom:FeatureList']);
  }

  // signout function
  const cognitoSignOut = async () => {
    try {
      await postLoginLogout({
        action: "logout",
        userName
      });
    } catch (err) {
      console.error("Logout audit failed", err);
    } finally {
      sessionStorage.removeItem(LOGIN_AUDIT_KEY);
      Auth.signOut();
    }
  };


  // return application component when login is valid
  if (userSession && userSession?.isValid()) {

    return (
      user && canUserAccess() ? (
        <FeatureProvider>
          <FeatureLoader userName={userName}>
            <Suspense fallback="loading...">
              <div className="app-shell">
                <Header signout={cognitoSignOut} user={user} />

                {/* The part that should fill the rest of the screen */}
                <main className="app-main">
                  <AppRoutes />
                </main>

                <ToastContainer
                  autoClose={3000}
                  hideProgressBar
                  newestOnTop={false}
                  closeOnClick
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  style={{ top: '100px' }}
                />
              </div>
            </Suspense>
          </FeatureLoader>
        </FeatureProvider>
      ) : (
        <PermissionError />
      )
    );

  } else {

    // Navigate to signin if login is not valid
    return (
      <div>
        You will be automatically redirected or{" "}
        <a href="#" onClick={Auth.federatedSignIn}>
          click here to login
        </a>
      </div>
    );

  }
}

export default CApp;